import { createKey, get } from "@/utils/networkCache";
import { request } from "leatherman-js";
import Status from "@/classes/Status";

const CACHE_EXPIRY = 60 * 60 * 1000; // 1 hour

const packages = {
  namespaced: true,
  state: {
    status: new Status(),
    list: [],
    item: null,
    itemStatus: new Status(),
    devListStatus: new Status(),
    devList: [],
  },
  mutations: {
    SET_CHUNK(state, payload) {
      const pageIndex = payload?.pageIndex;
      const pageSize = payload?.pageSize;
      const sortDirection = payload?.sortDirection;
      const sortProperty = payload?.sortProperty;
      const pagination = pageIndex || pageSize ? { index: pageIndex, size: pageSize } : null;
      const sort =
        sortDirection || sortProperty ? { direction: sortDirection, property: sortProperty } : null;
      state.chunk = pagination || sort ? { pagination, sort } : null;
    },
    SET_ITEM(state, payload) {
      state.item = payload;
    },
    SET_ITEM_STATUS(state, payload) {
      state.itemStatus.value = payload;
    },
    SET_LIST(state, payload) {
      state.list = payload;
    },
    SET_PAGINATION(state, payload) {
      state.pagination = payload;
    },
    SET_SORT(state, payload) {
      const sort = {};
      if (payload?.sortProperty) sort.property = payload?.sortProperty;
      if (payload?.sortDirection) sort.direction = payload?.sortDirection;
      state.sort = payload?.sortProperty || payload?.sortDirection ? sort : null;
    },
    SET_STATUS(state, payload) {
      state.status.value = payload;
    },
    SET_DEV_LIST(state, payload) {
      state.devList = payload;
    },
    SET_DEV_LIST_STATUS(state, payload) {
      state.devListStatus.value = payload;
    },
  },
  actions: {
    async get({ commit }, id) {
      commit("SET_ITEM_STATUS", Status.LOADING);
      try {
        const response = await get(createKey("package", { id }), request.makeParse, [
          "getPackage",
          { id },
        ]);
        commit("SET_ITEM", response);
        commit("SET_ITEM_STATUS", Status.LOADED);
        return response;
      } catch (e) {
        commit("SET_ITEM_STATUS", Status.ERROR);
      }
    },
    async getList({ commit }, params) {
      commit("SET_STATUS", Status.LOADING);
      commit("SET_CHUNK", params);
      commit("SET_SORT", params);
      try {
        const response = await get(
          createKey("packages", params),
          request.makeParse,
          ["getPackages", params],
          CACHE_EXPIRY
        );
        commit("SET_LIST", response.list);
        commit("SET_PAGINATION", response.pagination);
        commit("SET_STATUS", Status.LOADED);
        return response;
      } catch (e) {
        commit("SET_STATUS", Status.ERROR);
      }
    },
    async getDevList({ commit }, { environmentType }) {
      commit("SET_DEV_LIST_STATUS", Status.LOADING);
      // default to development
      environmentType = environmentType ? environmentType : "development";
      try {
        const response = await get(
          createKey("dev-packages", { environmentType }),
          request.makeParse,
          ["getDevPackages", { environmentType }]
        );
        commit("SET_DEV_LIST", response.list);
        commit("SET_DEV_LIST_STATUS", Status.LOADED);
        return response;
      } catch (e) {
        commit("SET_DEV_LIST_STATUS", Status.ERROR);
      }
    },
  },
  getters: {
    getItem: (state) => state.item,
    getItemStatus: (state) => state.itemStatus,
    getList: (state) => state.list,
    getListStatus: (state) => state.status,
    getPlanPackageOptions: (state) => (pkg, isTShirt, appId) => {
      const getPrice = (pkg) => pkg.pricing["1"];
      const packagePrefix = pkg.label.substr(0, pkg.label.indexOf("."));
      return state.list
        .filter((item) => {
          const allowed = item.restrictions?.apps
            ? item.restrictions?.apps.filter((app) => app.id === appId).length > 0
            : true;
          return (
            allowed &&
            item.environmentType === "production" &&
            item.type === "virt-guest-cloud" &&
            item.isTShirt === isTShirt &&
            !item.isFreeTrial &&
            item.label.startsWith(packagePrefix) &&
            item.isDedicated === pkg.isDedicated
          );
        })
        .sort((a, b) => (getPrice(a) > getPrice(b) ? 1 : -1));
    },
    getDevList: (state) => state.devList,
    getDevListStatus: (state) => state.devListStatus,
    getPackageById: (state) => (packageId) => state.list.find(({ id }) => id === packageId),
  },
};

export default packages;
